<template>
  <div class="patient-communication-tab">
    <!-- AI Summary Section -->
    <div class="ai-summary-section">
      <div class="section-header">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M13 2L3 14h8l-1 4 10-12h-8l1-4z" stroke="#01645a" stroke-width="1.5" stroke-linecap="round"
            stroke-linejoin="round" />
        </svg>
        <h3>AI tóm tắt</h3>
      </div>

      <!-- Chọn khoảng ngày rồi bấm chạy, không tự gọi AI -->
      <div class="ai-toolbar">
        <label class="filter-label">Hội thoại trong:</label>
        <select v-model.number="summaryDays" class="filter-select" :disabled="analysisLoading">
          <option v-for="d in dayOptions" :key="d" :value="d">{{ d }} ngày gần nhất</option>
        </select>
        <button class="ai-run-btn" :disabled="analysisLoading" @click="runAnalysis(false)">
          Phân tích
        </button>
        <button v-if="hasAnalysis" class="ai-regen-btn" :disabled="analysisLoading" @click="runAnalysis(true)">
          Tạo lại
        </button>
      </div>

      <div class="summary-content">
        <!-- Loading State -->
        <div v-if="analysisLoading" class="analysis-loading">
          <div class="loading-spinner"></div>
          <p>{{ loadingMessage }}</p>
        </div>

        <!-- Error State -->
        <div v-if="analysisError" class="analysis-error">
          <p>❌ {{ analysisError }}</p>
          <button class="retry-btn" @click="retryAnalysis">Thử lại</button>
        </div>

        <!-- Analysis Result -->
        <!-- Thẻ custom: bác sĩ tự hỏi, dùng khoảng ngày ở trên, không cache -->
        <div v-if="currentAnalysisType === 'custom'" class="summary-block">
          <h4>✍️ Hỏi AI về hội thoại</h4>
          <textarea
            v-model="customQuestion"
            class="custom-question-input"
            rows="3"
            :maxlength="customQuestionMax"
            placeholder="VD: Khách có than phiền gì về thuốc không? Những ngày nào đường huyết trên 10?"
            :disabled="customLoading"
            @keydown.ctrl.enter="askCustom"
          ></textarea>
          <div class="custom-question-actions">
            <button class="ai-run-btn" :disabled="customLoading || !customQuestion.trim()" @click="askCustom">
              {{ customLoading ? 'Đang hỏi...' : 'Hỏi AI' }}
            </button>
            <span class="analysis-meta">Dùng hội thoại {{ summaryDays }} ngày gần nhất · {{ customQuestion.length }}/{{ customQuestionMax }} ký tự</span>
          </div>
          <div v-if="customLoading" class="analysis-loading">
            <div class="loading-spinner"></div>
            <p>Đang đọc hội thoại và trả lời...</p>
          </div>
          <div v-else-if="customError" class="analysis-error">
            <p>❌ {{ customError }}</p>
          </div>
          <div v-else-if="customAnswer" class="analysis-result">
            <p class="analysis-meta">Trả lời dựa trên {{ customAnswerDays }} ngày gần nhất</p>
            <div v-html="formatAnalysisResult(customAnswer)"></div>
          </div>
        </div>

        <div v-if="!analysisLoading && !analysisError && displayedResult && currentAnalysisType !== 'custom'"
             class="summary-block" 
             ref="analysisResultBox">
          <h4>
            {{ getAnalysisTitle(currentAnalysisType) }}
            <span class="analysis-meta">· {{ summaryDays }} ngày gần nhất</span>
          </h4>
          <div class="analysis-result" v-html="formatAnalysisResult(displayedResult)"></div>
        </div>

        <div v-if="!analysisLoading && !analysisError && !hasAnalysis && currentAnalysisType !== 'custom'" class="summary-block">
          <p class="summary-description">Chọn khoảng thời gian và bấm "Phân tích" để AI tóm tắt hội thoại.</p>
        </div>

        <div class="summary-block">
          <h4>Các vấn đề sức khỏe thường được thảo luận</h4>
          <p class="summary-description">Nhấn vào một chủ đề để xem phân tích chi tiết</p>
          <div class="health-topics">
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'summary' }]"
              @click="analyzeHealth('summary')"
              :disabled="isTopicButtonsDisabled"
            >
              Tóm tắt
            </button>
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'bloodsugar' }]"
              @click="analyzeHealth('bloodsugar')"
              :disabled="isTopicButtonsDisabled"
            >
              Quản lý đường huyết
            </button>
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'bloodpresssure' }]"
              @click="analyzeHealth('bloodpresssure')"
              :disabled="isTopicButtonsDisabled"
            >
              Kiểm soát huyết áp
            </button>
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'nutrition' }]"
              @click="analyzeHealth('nutrition')"
              :disabled="isTopicButtonsDisabled"
            >
              Thực đơn dinh dưỡng
            </button>
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'movement' }]"
              @click="analyzeHealth('movement')"
              :disabled="isTopicButtonsDisabled"
            >
              Vận động
            </button>
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'sleep' }]"
              @click="analyzeHealth('sleep')"
              :disabled="isTopicButtonsDisabled"
            >
              Chất lượng giấc ngủ
            </button>
            <button
              :class="['topic-btn', { active: currentAnalysisType === 'custom' }]"
              @click="currentAnalysisType = 'custom'"
              :disabled="isTopicButtonsDisabled"
            >
              ✍️ Tùy chỉnh
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Grid -->
    <div class="main-content-grid">
      <!-- Conversation History -->
      <div class="conversation-history-section">
        <div class="section-header">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-1l-4 4z"
              stroke="#111827" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <h3>Lịch sử trò chuyện</h3>
        </div>

        <ZaloChatBox
          ref="zaloChatBox"
          :accountId="accountId"
          :preventInitialScroll="preventInitialScroll"
          @media-updated="handleImages"
          @coachIdSelected="coachId = $event"
          @search-query-changed="handleSearchQuery"
          @search-results="handleSearchResults"
          @search-cleared="handleSearchCleared"
          @message-jumped="handleMessageJumped"
          @thread-id-updated="handleThreadIdUpdated"
        />

        <!-- Shared Media Section -->
        <div class="shared-media-section">
          <div class="section-header">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
              <rect x="3" y="3" width="14" height="14" rx="2" stroke="currentColor" stroke-width="1.5" />
              <circle cx="8" cy="8" r="2" stroke="currentColor" stroke-width="1.5" />
              <path d="M17 13l-4-4-6 6-4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <h3>Hình ảnh được chia sẻ ({{ filteredMediaItems.length }})</h3>
          </div>

          <div class="image-filter-tabs">
            <div class="filter-dropdown-wrapper">
              <label class="filter-label">Người chia sẻ:</label>
              <select v-model="senderFilter" class="filter-select">
                <option value="all">Tất cả</option>
                <option value="user">Người dùng</option>
                <option value="oa">OA</option>
              </select>
            </div>

            <button :class="['filter-tab', { active: imageFilter === 'image' }]" @click="imageFilter = 'image'">
              Ảnh
            </button>
            <button :class="['filter-tab', { active: imageFilter === 'video' }]" @click="imageFilter = 'video'">
              Video
            </button>
            <button :class="['filter-tab', { active: imageFilter === 'file' }]" @click="imageFilter = 'file'">
              File
            </button>
          </div>

          <p class="image-subtitle">Nhấn vào để xem hình ảnh trong lịch sử tư vấn</p>

          <div class="images-grid">
            <div v-for="image in filteredMediaItems" :key="image.id" class="media-item"
              @click="handleMediaClick(image)">
              <div class="media-placeholder">
                <img v-if="image.eventName === 'user_send_group_image' || image.eventName === 'oa_send_group_image'"
                  :src="image.vngUrl ?? image.url" alt="shared image" class="media-thumb" loading="lazy"
                  @error="onMediaError" />

                <div
                  v-else-if="image.eventName === 'user_send_group_video' || image.eventName === 'oa_send_group_video'"
                  class="video-thumb">
                  <video :src="image.vngUrl ?? image.url" class="video-preview" />
                  <div class="play-icon">▶</div>
                </div>

                <div v-else class="file-thumb">
                  <span class="file-icon">{{ getFileIcon(image.vngUrl ?? image.url) }}</span>
                  <span class="file-name">{{ getFileName(image.vngUrl ?? image.url) }}</span>
                </div>
              </div>

              <div class="media-info">
                <span class="media-date">{{ image.sender }} gửi lúc {{ image.date }}</span>
              </div>
            </div>
          </div>

          <div v-if="filteredMediaItems.length === 0" class="no-media-data">
            <p>Không có nội dung phù hợp</p>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="sidebar">
        <div class="sidebar-section">
          <div class="section-header">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="3" y="4" width="14" height="14" rx="2" stroke="currentColor" stroke-width="1.5" />
              <path d="M3 8h14M8 2v4M12 2v4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <h3>Tư vấn gần đây</h3>
          </div>

          <div class="consultation-list">
            <div v-for="consultation in recentConsultations" :key="consultation.id" class="consultation-item">
              <div class="consultation-header">
                <h4>{{ consultation.name }}</h4>
                <span :class="{
                  'status-badge': true,
                  'complete': consultation.complete,
                  'scheduled': !consultation.complete
                }">
                  {{ consultation.complete ? 'Hoàn thành' : 'Đã lên lịch' }}
                </span>
              </div>
              <div class="consultation-date">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect x="2" y="3" width="10" height="9" rx="1" stroke="currentColor" stroke-width="1.2" />
                  <path d="M2 6h10M5 1v2M9 1v2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" />
                </svg>
                {{ formatDateTime(consultation.appointmentDate) }}
              </div>
              <p class="consultation-description"><b>Nội dung: </b>{{ consultation.goal }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- IMAGE PREVIEW MODAL -->
    <div
      v-if="previewImage && (previewImage.eventName === 'user_send_group_image' || previewImage.eventName === 'oa_send_group_image')"
      class="media-preview-overlay" @click.self="closePreview">
      <div class="media-preview-content">
        <button class="close-btn" @click="closePreview">✕</button>
        <img :src="previewImage.url" :alt="previewImage.sender || 'Preview'" class="preview-image" />
        <div v-if="previewImage.sender" class="preview-info">
          <p>{{ previewImage.sender }} - {{ previewImage.date }}</p>
        </div>
      </div>
    </div>

    <!-- VIDEO PREVIEW MODAL -->
    <div
      v-if="previewImage && (previewImage.eventName === 'user_send_group_video' || previewImage.eventName === 'oa_send_group_video')"
      class="media-preview-overlay" @click.self="closePreview">
      <div class="media-preview-content">
        <button class="close-btn" @click="closePreview">✕</button>
        <video :src="previewImage.url" class="preview-video" controls autoplay></video>
        <div v-if="previewImage.sender" class="preview-info">
          <p>{{ previewImage.sender }} - {{ previewImage.date }}</p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import ZaloChatBox from './component/ZaloChatBox.vue';
import { ONBOARDING } from '../customer_receives/CustomerReceiveEnum';
import { convertTimestampToDateNoTime } from '../../../core/utils/dateUtils';

export default {
  name: 'PatientCommunicationTab',
  components: {
    ZaloChatBox
  },
  props: {
    patientId: {
      type: String,
      default: ''
    },
    accountId: {
      type: String,
      default: ''
    },
    patientInfo: {
      type: Object,
      default: () => ({})
    },
    preventInitialScroll: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      imageFilter: 'image',
      senderFilter: 'all',
      groupImages: [],
      previewImage: null,
      coachId: null,
      recentConsultations: [],
      currentSearchQuery: '',
      searchResultsCount: 0,
      
      // AI Analysis State
      analysisLoading: false,
      analysisError: null,
      displayedResult: '',
      currentAnalysisType: null,
      loadingMessage: 'Đang phân tích...',
      threadId: '',
      summaryDays: 14,
      dayOptions: [7, 14, 30, 60, 90],

      // Kết quả 6 chủ đề của khoảng ngày đang chọn
      cachedAnalysisData: {},

      // Thẻ custom: không lưu cache
      customQuestion: '',
      customQuestionMax: 3000, // khớp giới hạn BE

      customAnswer: '',
      customAnswerDays: null,
      customLoading: false,
      customError: null,
      
      // Add refresh state
      isRefreshing: false,
      lastUpdatedTime: Date.now(),
    };
  },
  watch: {
    coachId() {
      this.loadBookedMeeting();
    },
    threadId(newThreadId) {
      if (newThreadId) {
        console.log('🔗 ThreadId updated:', newThreadId);
        // ThreadId changed - no need to auto-call API
        // User will click topic button or preload will handle it
      }
    },
    // Đổi khoảng ngày: chỉ hiện kết quả đã lưu của khoảng đó, không tự gọi AI
    summaryDays() {
      this.analysisError = null;
      this.cachedAnalysisData = this.loadFromLocalStorage() || {};
      this.displayedResult = this.cachedAnalysisData[this.currentAnalysisType] || '';
    }
  },
  computed: {
    isTopicButtonsDisabled() {
      return this.analysisLoading;
    },
    hasAnalysis() {
      return Object.keys(this.cachedAnalysisData).length > 0;
    },
    filteredMediaItems() {
      let items = this.groupImages || [];

      if (this.senderFilter === 'user') {
        items = items.filter(item => item.eventName?.startsWith('user_'));
      } else if (this.senderFilter === 'oa') {
        items = items.filter(item => item.eventName?.startsWith('oa_'));
      }

      if (this.imageFilter === 'image') {
        items = items.filter(item =>
          item.eventName === 'user_send_group_image' ||
          item.eventName === 'oa_send_group_image'
        );
      } else if (this.imageFilter === 'video') {
        items = items.filter(item =>
          item.eventName === 'user_send_group_video' ||
          item.eventName === 'oa_send_group_video'
        );
      } else if (this.imageFilter === 'file') {
        items = items.filter(item =>
          !item.eventName?.includes('_image') &&
          !item.eventName?.includes('_video') &&
          !item.eventName?.includes('_text') &&
          !item.eventName?.includes('_sticker')
        );
      }

      return items;
    }
  },
  methods: {
    // ========== REFRESH ALL DATA ==========
    async refreshAllData() {
      if (this.isRefreshing) {
        console.log('⏳ Refresh already in progress...');
        return;
      }

      this.isRefreshing = true;
      console.log('🔄 Starting full refresh...');

      try {
        // 1. Clear AI analysis cache (memory and localStorage)
        this.cachedAnalysisData = {};
        this.displayedResult = '';
        this.currentAnalysisType = 'summary';
        this.analysisError = null;
        this.clearLocalStorageCache();
        console.log('✅ Cleared AI cache');

        // 2. Reload ZaloChatBox (trigger child component refresh)
        if (this.$refs.zaloChatBox && typeof this.$refs.zaloChatBox.refreshChat === 'function') {
          await this.$refs.zaloChatBox.refreshChat();
          console.log('✅ Refreshed chat messages');
        }

        // 3. Reload recent consultations
        if (this.coachId) {
          await this.loadBookedMeeting();
          console.log('✅ Reloaded consultations');
        }

        // 4. AI không tự chạy lại, bác sĩ bấm "Phân tích" khi cần

        // 5. Update last updated time
        this.lastUpdatedTime = Date.now();
        
        // 6. Emit event to parent component to refresh its data
        this.$emit('data-refreshed', this.lastUpdatedTime);

        this.$toastr.s({
          title: 'Làm mới thành công',
          msg: 'Đã cập nhật tất cả dữ liệu mới nhất',
        });

        console.log('✅ Full refresh completed');
      } catch (error) {
        console.error('❌ Refresh failed:', error);
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không thể làm mới dữ liệu. Vui lòng thử lại.',
        });
      } finally {
        this.isRefreshing = false;
      }
    },

    formatDateTime(timestamp) {
      return convertTimestampToDateNoTime(timestamp);
    },

    handleImages(images) {
      this.groupImages = images;
    },

    handleSearchQuery(query) {
      this.currentSearchQuery = query;
    },

    handleSearchResults(results) {
      this.searchResultsCount = results.length;
    },

    handleSearchCleared() {
      this.currentSearchQuery = '';
      this.searchResultsCount = 0;
    },

    handleMessageJumped(messageId) {
      console.log('🎯 Jumped to message:', messageId);
    },

    handleThreadIdUpdated(threadId) {
      console.log('🔗 Thread ID received:', threadId);
      this.threadId = threadId;
    },

    // ========== AI ANALYSIS WITH CACHE ==========
    // Bấm chủ đề chỉ hiển thị kết quả đã có, không gọi AI
    analyzeHealth(analysisType) {
      this.currentAnalysisType = analysisType;
      this.displayedResult = this.cachedAnalysisData[analysisType]
        || (this.hasAnalysis ? 'Không có dữ liệu cho chủ đề này' : '');
    },

    // Thẻ custom: hỏi thẳng LLM theo khoảng ngày đang chọn, mỗi lần đều gọi mới
    async askCustom() {
      const question = this.customQuestion.trim();
      if (!question || this.customLoading) return;

      this.customLoading = true;
      this.customError = null;
      this.customAnswer = '';
      const days = this.summaryDays;

      const formData = new FormData();
      formData.append('Message', question);
      formData.append('AccountId', this.accountId);
      formData.append('Days', days);

      try {
        const data = await this.$api.post('/AI/AgentCustomAsk', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (!data || !data.success) throw new Error(data?.error || 'Không hỏi được AI');

        // Câu trả lời có thể lặp lại nội dung bác sĩ gõ -> escape trước khi v-html
        this.customAnswer = String(data.response || '')
          .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        this.customAnswerDays = data.days || days;
      } catch (error) {
        console.error('❌ AgentCustomAsk failed:', error);
        this.customError = error.message || 'Có lỗi xảy ra khi hỏi AI';
      } finally {
        this.customLoading = false;
      }
    },

    // force = true: BE bỏ qua cache 1h, chạy lại AI và ghi đè
    async runAnalysis(force = false) {
      // Đang ở thẻ custom mà bấm "Phân tích" thì quay về Tóm tắt
      if (!this.currentAnalysisType || this.currentAnalysisType === 'custom') this.currentAnalysisType = 'summary';
      this.analysisLoading = true;
      this.analysisError = null;
      this.displayedResult = '';
      this.loadingMessage = this.getLoadingMessage(this.currentAnalysisType);

      try {
        await this.callAgentSummary(force);
        this.saveToLocalStorage();
      } catch (error) {
        console.error('❌ Error:', error.message);
        this.analysisError = error.message || 'Có lỗi xảy ra khi phân tích';
      } finally {
        this.analysisLoading = false;
      }
    },

    // ========== LOCAL STORAGE METHODS ==========
    // Mỗi khoảng ngày 1 key, giữ 1h như cache BE
    getLocalStorageKey() {
      return `ai_analysis_cache_${this.accountId}_${this.summaryDays}d`;
    },

    saveToLocalStorage() {
      if (!this.accountId || Object.keys(this.cachedAnalysisData).length === 0) return;
      try {
        const cacheData = {
          data: this.cachedAnalysisData,
          timestamp: Date.now(),
          accountId: this.accountId
        };
        localStorage.setItem(this.getLocalStorageKey(), JSON.stringify(cacheData));
      } catch (error) {
        console.error('❌ Failed to save to localStorage:', error);
      }
    },

    loadFromLocalStorage() {
      if (!this.accountId) return null;
      try {
        const cached = localStorage.getItem(this.getLocalStorageKey());
        if (!cached) return null;

        const cacheData = JSON.parse(cached);
        const maxAge = 60 * 60 * 1000; // 1 giờ
        if (Date.now() - cacheData.timestamp > maxAge) {
          localStorage.removeItem(this.getLocalStorageKey());
          return null;
        }
        return cacheData.data;
      } catch (error) {
        console.error('❌ Failed to load from localStorage:', error);
        return null;
      }
    },

    clearLocalStorageCache() {
      if (!this.accountId) return;
      try {
        localStorage.removeItem(this.getLocalStorageKey());
      } catch (error) {
        console.error('❌ Failed to clear localStorage:', error);
      }
    },

    async callAgentSummary(force = false) {
      const formData = new FormData();
      formData.append('KpiType', 'summary_conversation');
      formData.append('Message', 'Phân tích');
      formData.append('AccountId', this.accountId);
      formData.append('Days', this.summaryDays);
      formData.append('ForceRefresh', force);

      try {
        const data = await this.$api.post('/AI/AgentSummary', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (!data || !data.success) {
          throw new Error(data?.error || 'API response indicates failure');
        }

        let parsedData = data.response;
        if (typeof parsedData === 'string') {
          parsedData = JSON.parse(parsedData);
        }

        // Cache hit BE trả [{text: {...}}], chạy mới trả thẳng {summary: "...", ...}
        const source = Array.isArray(parsedData) ? parsedData[0]?.text : parsedData;
        const result = {};
        Object.keys(source || {}).forEach(type => {
          if (source[type]) result[type] = source[type];
        });
        this.cachedAnalysisData = result;

        this.displayedResult = result[this.currentAnalysisType] || 'Không có dữ liệu cho chủ đề này';

        this.$toastr.s({
          title: 'Thành công',
          msg: data.fromCache ? 'Đã lấy kết quả phân tích gần nhất' : 'Phân tích hoàn thành'
        });
      } catch (error) {
        console.error('❌ AgentSummary failed:', error);
        this.$toastr.e({
          title: 'Lỗi',
          msg: 'Không thể phân tích dữ liệu'
        });
        throw error;
      }
    },

    retryAnalysis() {
      this.runAnalysis(false);
    },

    getLoadingMessage(type) {
      const messages = {
        'summary': 'Đang tổng hợp dữ liệu sức khỏe...',
        'bloodsugar': 'Đang phân tích xu hướng đường huyết...',
        'bloodpresssure': 'Đang phân tích huyết áp...',
        'nutrition': 'Đang xây dựng thực đơn dinh dưỡng...',
        'movement': 'Đang tạo kế hoạch vận động...',
        'sleep': 'Đang phân tích chất lượng giấc ngủ...'
      };
      return messages[type] || 'Đang phân tích...';
    },

    getAnalysisTitle(type) {
      const titles = {
        'summary': '📊 Tóm tắt sức khỏe',
        'bloodsugar': '🩸 Phân tích đường huyết',
        'bloodpresssure': '💗 Phân tích huyết áp',
        'nutrition': '🥗 Khuyến nghị dinh dưỡng',
        'movement': '🏃 Kế hoạch vận động',
        'sleep': '😴 Chất lượng giấc ngủ'
      };
      return titles[type] || 'Kết quả phân tích';
    },

    formatAnalysisResult(result) {
      if (!result) return '';
      
      return result
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        .replace(/- /g, '• ');
    },

    // ========== MEDIA METHODS ==========
    handleMediaClick(media) {
      const isImage = media.eventName === 'user_send_group_image' || media.eventName === 'oa_send_group_image';
      const isVideo = media.eventName === 'user_send_group_video' || media.eventName === 'oa_send_group_video';
      
      if (isImage) {
        this.openImagePreview(media);
      } else if (isVideo) {
        this.openVideoPreview(media);
      } else {
        window.open(media.url, '_blank');
      }
    },

    openImagePreview(image) {
      const scrollY = window.scrollY;
      this.previewImage = image;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    },

    openVideoPreview(video) {
      const scrollY = window.scrollY;
      this.previewImage = video;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    },

    closePreview() {
      const scrollY = document.body.style.top;
      this.previewImage = null;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    },

    getFileName(url) {
      return url.split('/').pop() || 'File';
    },

    getFileIcon(url) {
      const fileName = this.getFileName(url).toLowerCase();
      if (fileName.endsWith('.pdf')) return '📕';
      if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) return '📄';
      if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) return '📊';
      if (fileName.endsWith('.ppt') || fileName.endsWith('.pptx')) return '📈';
      if (fileName.endsWith('.zip') || fileName.endsWith('.rar')) return '📦';
      if (fileName.endsWith('.txt')) return '📝';
      return '📄';
    },

    onMediaError(event) {
      event.target.style.opacity = '0.5';
      event.target.style.filter = 'grayscale(100%)';
    },

    async loadBookedMeeting() {
      try {
        const { data } = await this.$api.get('Calendar/BookedByAccountId', {
          params: {
            CoachId: this.coachId,
            AccountId: this.accountId,
            CourseId: ONBOARDING.ID
          },
        });
        this.recentConsultations = data?.map(el => ({
          ...el,
        })) || [];
      } catch (error) {
        this.$toastr.e({
          title: 'Lỗi',
          msg: error,
        });
      }
    }
  },

  mounted() {
    console.log('🚀 Component mounted');

    // Chỉ hiện kết quả đã lưu (< 1h), không tự gọi AI
    this.currentAnalysisType = 'summary';
    this.cachedAnalysisData = this.loadFromLocalStorage() || {};
    this.displayedResult = this.cachedAnalysisData['summary'] || '';
  },

  beforeUnmount() {
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  }
};
</script>

<style scoped>
/* Base Layout */
.patient-communication-tab {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Section Common Styles */
.ai-summary-section,
.conversation-history-section,
.sidebar-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.ai-summary-section {
  border: 1px solid #e5e7eb;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.section-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

/* AI Summary */
.summary-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.summary-block h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.75rem;
}

.summary-block ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.summary-block li {
  padding-left: 1.25rem;
  position: relative;
  font-size: 0.875rem;
  line-height: 1.6;
  color: #374151;
}

.summary-block li::before {
  content: '•';
  position: absolute;
  left: 0;
  color: #01645a;
  font-weight: bold;
}

.summary-description {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

/* Analysis States */
.analysis-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #01645a;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.analysis-loading p {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
}

.analysis-error {
  padding: 1.5rem;
  background: #fee2e2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  text-align: center;
}

.analysis-error p {
  color: #991b1b;
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
}

.retry-btn {
  padding: 0.5rem 1rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.retry-btn:hover {
  background: #b91c1c;
}

.ai-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.ai-run-btn,
.ai-regen-btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.ai-run-btn {
  background: #01645a;
  color: white;
  border: none;
}

.ai-regen-btn {
  background: white;
  color: #01645a;
  border: 1px solid #01645a;
}

.ai-run-btn:disabled,
.ai-regen-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.analysis-meta {
  font-weight: 400;
  color: #6b7280;
}

.custom-question-input {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  resize: vertical;
}

.custom-question-input:focus {
  outline: none;
  border-color: #01645a;
}

.custom-question-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0.5rem 0 1rem;
  font-size: 0.8125rem;
}

.analysis-result {
  font-size: 1rem;
  line-height: 1.8;
  color: #374151;
  white-space: pre-wrap;
  max-height: 500px;
  overflow-y: auto;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  font-weight: 600;
}

/* Health Topics */
.health-topics {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.topic-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  font-size: 0.8125rem;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
}

.topic-btn:hover:not(:disabled) {
  border-color: #01645a;
  color: #01645a;
}

.topic-btn.active {
  background: #01645a;
  border-color: #01645a;
  color: white;
}

.topic-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Main Content Grid */
.main-content-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 1.5rem;
  align-items: start;
}

/* Conversation History Section */
.conversation-history-section {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.shared-media-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.no-data {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
}

.no-data p {
  margin: 0;
}

.no-media-data {
  text-align: center;
  padding: 2rem;
  color: #9ca3af;
  background: #f9fafb;
  border-radius: 8px;
  margin-top: 1rem;
}

.no-media-data p {
  margin: 0;
  font-size: 0.875rem;
}

/* Sidebar */
.sidebar {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

/* Consultations */
.consultation-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.consultation-item {
  padding: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
}

.consultation-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.consultation-header h4 {
  font-size: 0.875rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  flex: 1;
}

.status-badge {
  padding: 0.25rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  white-space: nowrap;
}

.status-badge.complete {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.scheduled {
  background: #e0e7ff;
  color: #3730a3;
}

.consultation-date {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.consultation-description {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #374151;
  margin: 0;
}

/* Image Filter Tabs */
.image-filter-tabs {
  display: inline-flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.filter-dropdown-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-right: auto;
}

.filter-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}

.filter-select {
  padding: 0.5rem 0.75rem;
  font-size: 0.8125rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
  color: #374151;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
}

.filter-select:hover {
  border-color: #01645a;
}

.filter-select:focus {
  outline: none;
  border-color: #01645a;
  box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.1);
}

.filter-tab {
  padding: 0.5rem 0.75rem;
  border: none;
  background: none;
  font-size: 0.8125rem;
  color: #6b7280;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s;
}

.filter-tab:hover {
  color: #01645a;
}

.filter-tab.active {
  color: #01645a;
  border-bottom-color: #01645a;
  font-weight: 600;
}

.image-subtitle {
  font-size: 0.8125rem;
  color: #6b7280;
  margin-bottom: 1rem;
}

/* Media Grid */
.images-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.75rem;
}

.media-item {
  cursor: pointer;
  transition: transform 0.2s;
}

.media-item:hover {
  transform: scale(1.05);
}

.media-placeholder {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.media-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: opacity 0.2s;
}

.video-thumb {
  position: relative;
  width: 100%;
  height: 100%;
}

.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 32px;
  color: white;
  background: rgba(0, 0, 0, 0.6);
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.file-thumb {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
  padding: 16px;
  text-align: center;
  background: #f9fafb;
}

.file-icon {
  font-size: 32px;
}

.file-name {
  font-size: 12px;
  color: #666;
  word-break: break-word;
  line-height: 1.2;
}

.media-info {
  display: flex;
  align-items: center;
  margin-top: 0.375rem;
}

.media-date {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Preview Modal */
.media-preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: fadeIn 0.2s ease;
}

.media-preview-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.preview-video {
  max-width: 100%;
  max-height: 80vh;
  border-radius: 8px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  background: #000;
}

.close-btn {
  position: absolute;
  top: -50px;
  right: 0;
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 24px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  z-index: 10000;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.1);
}

.preview-info {
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  color: white;
  font-size: 0.875rem;
}

.preview-info p {
  margin: 0;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 1200px) {
  .main-content-grid {
    grid-template-columns: 1fr;
  }

  .images-grid {
    grid-template-columns: repeat(6, 1fr);
  }
}

@media (max-width: 768px) {
  .images-grid {
    grid-template-columns: repeat(6, 1fr);
  }

  .health-topics {
    gap: 0.375rem;
  }

  .topic-btn {
    padding: 0.375rem 0.75rem;
    font-size: 0.75rem;
  }

  .preview-image,
  .preview-video {
    max-height: 70vh;
  }

  .close-btn {
    top: -40px;
    width: 36px;
    height: 36px;
    font-size: 20px;
  }

  .image-filter-tabs {
    flex-wrap: wrap;
  }

  .filter-dropdown-wrapper {
    width: 100%;
    margin-bottom: 0.5rem;
  }

  .filter-select {
    flex: 1;
  }
}
</style>